import { callGateway, callGatewayForm, type CallerIdentity } from "./gateway";
import { config } from "../config";

/**
 * A new operator's own onboarding answers and its Onboarding verification fee
 * (owner 2026-10-30): non-refundable, paid before the online KYC. radaccess
 * binds every call to the token subject and the applicant.<id> namespace.
 */
/** Shown after sign-up and on the setup steps (owner 2026-10-31). */
export const RESUME_NOTICE =
  "You can resume this setup any time: sign in with your mobile number and a code, or with your username and password. Every finished step is saved.";

export interface OnboardingBusiness {
  legalname: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  shortname: string;
  /** "" for "I don't have a domain". */
  domain: string;
  /** Accepted: this namespace will not change in future (owner 2026-10-30). */
  declared: boolean;
  businesstype?: string;
}

export const EMPTY_BUSINESS: OnboardingBusiness = { legalname: "", address: "", city: "", state: "", pincode: "", shortname: "", domain: "", declared: false };

export interface OnboardingCheckout {
  key: string;
  orderid: string;
  name: string;
  email: string;
  contact: string;
}

export interface OnboardingState {
  /** False until Ankh opens self-onboarding: the steps are not shown. */
  available: boolean;
  editable: boolean;
  business: OnboardingBusiness;
  namespace: string;
  /** The short name goes under this, e.g. net. */
  parent: string;
  /** io.ankhos.<short>: how Ankh's books name this operator. */
  clientname?: string;
  domainverified: boolean;
  txtname?: string;
  txtvalue?: string;
  fee: { amount: number; status: "" | "Pending" | "Paid" | "Failed" | string; checkout?: OnboardingCheckout };
}

const base = (ns: string) => `/operator/${encodeURIComponent(ns)}/applicant`;

export const getOnboarding = (i: CallerIdentity, ns: string) => callGateway<OnboardingState>(i, config().gatewayBase, `${base(ns)}/onboarding`);

export const saveBusiness = (i: CallerIdentity, ns: string, body: OnboardingBusiness) =>
  callGateway<OnboardingState>(i, config().gatewayBase, `${base(ns)}/business`, { method: "PUT", body });

export interface ShortNameCheck {
  available: boolean;
  message?: string;
}

/** Is this short name free? Asked as it is typed; saves nothing. */
export const checkShortName = (i: CallerIdentity, ns: string, shortname: string, domain: string) =>
  callGateway<ShortNameCheck>(i, config().gatewayBase, `${base(ns)}/shortname`, { method: "POST", body: { shortname, domain } });

export const verifyDomain = (i: CallerIdentity, ns: string) =>
  callGateway<OnboardingState>(i, config().gatewayBase, `${base(ns)}/domain/verify`, { method: "POST", body: {} });

export const startFee = (i: CallerIdentity, ns: string) => callGateway<OnboardingState>(i, config().gatewayBase, `${base(ns)}/fee`, { method: "POST", body: {} });

export const confirmFee = (i: CallerIdentity, ns: string, paymentId: string, signature: string) =>
  callGateway<OnboardingState>(i, config().gatewayBase, `${base(ns)}/fee/confirm`, {
    method: "POST",
    body: { payment_id: paymentId, signature },
  });

/** The ID these answers give, as the declaration names it. Mirrors namespaceFor. */
export function namespacePreview(parent: string, domain: string, shortname: string): string {
  const short = shortname.trim().toLowerCase();
  const d = domain.trim().toLowerCase().replace(/\.$/, "");
  if (!d) return `${parent}.${short}`;
  return `${d.split(".").reverse().join(".")}.${short}`;
}

/** Mirrors radaccess validShortName. "" when it may be sent. */
export function shortNameProblem(typed: string): string {
  const s = typed.trim().toLowerCase();
  if (!/^[a-z][a-z0-9-]{1,28}[a-z0-9]$/.test(s)) return "3-30 letters, digits or hyphens, starting with a letter.";
  return "";
}

/** Everything wrong with the details, or [] when they may be sent. */
/** What the name field means for this type. A proprietor's name is on no document. */
export function businessNameLabel(t?: string): string {
  if (t === "individual") return "Your name";
  if (t === "proprietorship") return "Business name";
  return "Legal name";
}

export function businessProblems(b: OnboardingBusiness): string[] {
  const p: string[] = [];
  if (!b.businesstype) p.push("Choose your business type.");
  if (!b.legalname.trim()) p.push(`Enter ${businessNameLabel(b.businesstype).toLowerCase()}.`);
  if (!b.address.trim()) p.push("Enter your address.");
  if (!b.city.trim()) p.push("Enter your city.");
  if (!b.state.trim()) p.push("Enter your state.");
  if (!/^[1-9][0-9]{5}$/.test(b.pincode.trim())) p.push("Enter a 6-digit PIN code.");
  const s = shortNameProblem(b.shortname);
  if (s) p.push(`Short name: ${s}`);
  const d = b.domain.trim();
  if (d && !/^[a-z0-9-]+(\.[a-z0-9-]+)+\.?$/i.test(d)) p.push("Enter your main domain, like supernet.in.");
  if (!b.declared) p.push("Accept the declaration: this namespace will not change in future.");
  return p;
}

export interface KycCheck {
  status: "PENDING" | "PASSED" | "FAILED" | "REVIEW" | "TYPED";
  attemptsleft: number;
  method?: string;
  name?: string;
  last4?: string;
  address?: string;
  reason?: string;
  digilockerleft?: boolean;
}

export type BusinessType = "company" | "llp" | "partnership" | "other" | "proprietorship" | "individual";

/** A proprietor's and an individual's PAN is the person's own. */
export const personalBusiness = (t?: string) => t === "proprietorship" || t === "individual";

export const BUSINESS_TYPES: { value: BusinessType; label: string }[] = [
  { value: "company", label: "Company" },
  { value: "llp", label: "LLP" },
  { value: "partnership", label: "Partnership firm" },
  { value: "proprietorship", label: "Proprietorship" },
  { value: "individual", label: "Individual" },
  { value: "other", label: "Trust, society or other" },
];

export interface KycState {
  available: boolean;
  businesstype?: BusinessType;
  pan: KycCheck;
  ownerpan: KycCheck;
  aadhaar: KycCheck;
  bank: KycCheck;
  gstin: KycCheck;
  /** Older answers leave it out. */
  cin?: KycCheck;
  complete: boolean;
  review: boolean;
  /** The DigiLocker link, on the answer that started it. */
  url?: string;
}

const kyc = (ns: string) => `${base(ns)}/kyc`;

export const getKyc = (i: CallerIdentity, ns: string) => callGateway<KycState>(i, config().gatewayBase, kyc(ns));

export const verifyPan = (i: CallerIdentity, ns: string, businesstype: BusinessType, pan: string) =>
  callGateway<KycState>(i, config().gatewayBase, `${kyc(ns)}/pan`, { method: "POST", body: { businesstype, pan } });

export const verifyOwnerPan = (i: CallerIdentity, ns: string, pan: string) =>
  callGateway<KycState>(i, config().gatewayBase, `${kyc(ns)}/ownerpan`, { method: "POST", body: { pan } });

export const activateApplicant = (i: CallerIdentity, ns: string) =>
  callGateway<{ namespace: string; note: string }>(i, config().gatewayBase, `${base(ns)}/activate`, { method: "POST", body: {} });

export const startDigiLocker = (i: CallerIdentity, ns: string) =>
  callGateway<KycState>(i, config().gatewayBase, `${kyc(ns)}/aadhaar/digilocker`, { method: "POST", body: {} });

export const digiLockerResult = (i: CallerIdentity, ns: string) =>
  callGateway<KycState>(i, config().gatewayBase, `${kyc(ns)}/aadhaar/digilocker/result`, { method: "POST", body: {} });

export function uploadAadhaar(i: CallerIdentity, ns: string, file: File) {
  const form = new FormData();
  form.append("file", file);
  return callGatewayForm<KycState>(i, config().gatewayBase, `${kyc(ns)}/aadhaar/ocr`, form);
}

export const verifyBank = (i: CallerIdentity, ns: string, account: string, ifsc: string, confirmname?: string) =>
  callGateway<KycState>(i, config().gatewayBase, `${kyc(ns)}/bank`, { method: "POST", body: { account, ifsc, confirmname } });

export const verifyGstin = (i: CallerIdentity, ns: string, gstin: string) =>
  callGateway<KycState>(i, config().gatewayBase, `${kyc(ns)}/gstin`, { method: "POST", body: { gstin } });

/** A company's CIN is checked; an LLP's LLPIN is kept unverified. */
export const verifyCin = (i: CallerIdentity, ns: string, cin: string) =>
  callGateway<KycState>(i, config().gatewayBase, `${kyc(ns)}/cin`, { method: "POST", body: { cin } });

/** A DigiLocker journey was started and its outcome has not been read yet. */
export const digiLockerWaiting = (k: KycCheck) => k.status === "PENDING" && k.method === "digilocker";

/** Aadhaar's upload is offered first once DigiLocker has failed or is used up (owner 2026-10-30). */
export const uploadPreferred = (k: KycCheck) => k.status === "FAILED" && (k.method === "digilocker" || !k.digilockerleft);
